'use client';

import { createClient } from '@/lib/supabase/client';
import type { IllustrationAsset, BackgroundAsset } from '@/lib/illustrationTypes';

const supabase = createClient();

// ─── Illustration Assets ──────────────────────────────────────────────────────

export async function fetchActiveIllustrations(): Promise<IllustrationAsset[]> {
  try {
    const { data, error } = await supabase
      .from('illustration_assets')
      .select('*')
      .eq('is_active', true)
      .order('sort_order', { ascending: true });

    if (error) {
      console.error('Error fetching illustrations:', error.message);
      return [];
    }
    return (data || []).map(row => ({
      id: row.id,
      name: row.name,
      filePath: row.file_path,
      storageUrl: row.storage_url,
      isActive: row.is_active,
      sortOrder: row.sort_order,
      scale: row.scale ?? 1.0,
      createdAt: row.created_at,
    }));
  } catch {
    return [];
  }
}

export async function fetchAllIllustrations(): Promise<IllustrationAsset[]> {
  try {
    const { data, error } = await supabase
      .from('illustration_assets')
      .select('*')
      .order('sort_order', { ascending: true });

    if (error) {
      console.error('Error fetching illustrations:', error.message);
      return [];
    }
    return (data || []).map(row => ({
      id: row.id,
      name: row.name,
      filePath: row.file_path,
      storageUrl: row.storage_url,
      isActive: row.is_active,
      sortOrder: row.sort_order,
      scale: row.scale ?? 1.0,
      createdAt: row.created_at,
    }));
  } catch {
    return [];
  }
}

export async function uploadIllustration(file: File): Promise<IllustrationAsset | null> {
  try {
    const ext = file.name.split('.').pop() || 'png';
    const fileName = `${Date.now()}_${Math.random().toString(36).slice(2)}.${ext}`;
    const filePath = `illustrations/${fileName}`;

    const { error: uploadError } = await supabase.storage
      .from('illustrations')
      .upload(filePath, file, { cacheControl: '3600', upsert: false });

    if (uploadError) {
      console.error('Upload error:', uploadError.message);
      return null;
    }

    const { data: { publicUrl } } = supabase.storage
      .from('illustrations')
      .getPublicUrl(filePath);

    // Get current max sort_order
    const { data: existing } = await supabase
      .from('illustration_assets')
      .select('sort_order')
      .order('sort_order', { ascending: false })
      .limit(1);

    const nextOrder = existing && existing.length > 0 ? (existing[0].sort_order + 1) : 0;

    const { data, error: dbError } = await supabase
      .from('illustration_assets')
      .insert({
        name: file.name.replace(/\.[^.]+$/, ''),
        file_path: filePath,
        storage_url: publicUrl,
        is_active: true,
        sort_order: nextOrder,
        scale: 1.0,
      })
      .select()
      .single();

    if (dbError) {
      console.error('DB insert error:', dbError.message);
      return null;
    }

    return {
      id: data.id,
      name: data.name,
      filePath: data.file_path,
      storageUrl: data.storage_url,
      isActive: data.is_active,
      sortOrder: data.sort_order,
      scale: data.scale ?? 1.0,
      createdAt: data.created_at,
    };
  } catch (err) {
    console.error('Upload failed:', err);
    return null;
  }
}

export async function toggleIllustrationActive(id: string, isActive: boolean): Promise<boolean> {
  try {
    const { error } = await supabase
      .from('illustration_assets')
      .update({ is_active: isActive })
      .eq('id', id);
    if (error) {
      console.error('Toggle error:', error.message);
      return false;
    }
    return true;
  } catch {
    return false;
  }
}

export async function deleteIllustration(id: string, filePath: string): Promise<boolean> {
  try {
    // Remove from storage
    await supabase.storage.from('illustrations').remove([filePath]);
    // Remove from DB
    const { error } = await supabase
      .from('illustration_assets')
      .delete()
      .eq('id', id);
    if (error) {
      console.error('Delete error:', error.message);
      return false;
    }
    return true;
  } catch {
    return false;
  }
}

export async function updateIllustrationOrder(id: string, sortOrder: number): Promise<boolean> {
  try {
    const { error } = await supabase
      .from('illustration_assets')
      .update({ sort_order: sortOrder })
      .eq('id', id);
    if (error) return false;
    return true;
  } catch {
    return false;
  }
}

export async function updateIllustrationScale(id: string, scale: number): Promise<boolean> {
  try {
    const { error } = await supabase
      .from('illustration_assets')
      .update({ scale })
      .eq('id', id);
    if (error) {
      console.error('Scale update error:', error.message);
      return false;
    }
    return true;
  } catch {
    return false;
  }
}

// ─── Background Asset ─────────────────────────────────────────────────────────

export async function fetchBackground(): Promise<BackgroundAsset | null> {
  try {
    const { data, error } = await supabase
      .from('background_asset')
      .select('*')
      .order('updated_at', { ascending: false })
      .limit(1)
      .maybeSingle();

    if (error) {
      console.error('Error fetching background:', error.message);
      return null;
    }
    if (!data) return null;
    return {
      id: data.id,
      storageUrl: data.storage_url,
      updatedAt: data.updated_at,
    };
  } catch {
    return null;
  }
}

export async function uploadBackground(file: File): Promise<BackgroundAsset | null> {
  try {
    const ext = file.name.split('.').pop() || 'jpg';
    const fileName = `background_${Date.now()}.${ext}`;
    const filePath = `backgrounds/${fileName}`;

    const { error: uploadError } = await supabase.storage
      .from('backgrounds')
      .upload(filePath, file, { cacheControl: '3600', upsert: false });

    if (uploadError) {
      console.error('Background upload error:', uploadError.message);
      return null;
    }

    const { data: { publicUrl } } = supabase.storage
      .from('backgrounds')
      .getPublicUrl(filePath);

    // Delete old background records
    await supabase.from('background_asset').delete().neq('id', '00000000-0000-0000-0000-000000000000');

    const { data, error: dbError } = await supabase
      .from('background_asset')
      .insert({ storage_url: publicUrl })
      .select()
      .single();

    if (dbError) {
      console.error('Background DB error:', dbError.message);
      return null;
    }

    return {
      id: data.id,
      storageUrl: data.storage_url,
      updatedAt: data.updated_at,
    };
  } catch (err) {
    console.error('Background upload failed:', err);
    return null;
  }
}
